import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IFieldDefinition, IModuleDefinition, IRecordTypeDefinition } from '@platform/core/types';
import { useLabel } from '@platform/hooks/useLabel';
import { moduleNamespace } from '@platform/i18n/moduleBundle';
import { findModuleDefinition } from '@platform/modularity/moduleDefinitionRegistry';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { modelSourcesOf } from './dataModel';
import { entityAttrsOf, entityKindOf } from './entityAttrs';
import type { IModelAttr } from './IModelAttr';
import type { IModelEntity } from './IModelEntity';
import { useModelText } from './useModelText';

/**
 * Сутності «Моделі даних» модуля мовою інтерфейсу (прототип v4 pgMdesign, E): основний тип з його полями й статусами, далі сутності
 * прототипу (`designEntities` без `extra`; рядки пакета `entities.<тип>.designEntities.<i>`) з атрибутами прототипу (entityAttrsOf; підписи
 * й варіанти — modDesign.model.entAttr / entOpt), вид — за назвою (entityKindOf); без сутностей прототипу — інші типи записів модуля.
 */
export function useModelEntities(ws: string, module: IModuleDefinition): IModelEntity[] {
  const { t } = useTranslation();
  const label = useLabel();
  const key = module.module.key;
  const text = useModelText(ws, key);
  const strings = useModuleStrings(key);
  return useMemo(() => {
    const mainRt = module.recordTypes.find((r) => r.main);
    const own = mainRt?.designEntities ?? findModuleDefinition(key)?.recordTypes.find((r) => r.key === mainRt?.key)?.designEntities;
    const { main, entities, types } = modelSourcesOf(module, own);
    if (!main) return [];
    const fieldAttr = (rt: IRecordTypeDefinition, f: IFieldDefinition): IModelAttr => ({
      key: f.key, label: text.field(rt, f), type: text.type(f), typeName: text.typeName(f), ref: !!f.ref, required: !!f.required, options: text.options(rt, f),
    });
    const typeEntity = (rt: IRecordTypeDefinition, kind: IModelEntity['kind']): IModelEntity => ({
      key: rt.key, name: text.name(rt), description: text.description(rt), kind, attrs: rt.fields.filter((f) => !f.hidden).map((f) => fieldAttr(rt, f)), rt,
    });
    const head = typeEntity(main, 'main');
    const moduleName = strings.moduleName(module.module.name);
    const ns = moduleNamespace(key);
    const designed = (entities ?? []).map(({ entity, index }): IModelEntity => {
      const at = `entities.${main.key}.designEntities.${index}`;
      const nameEn = entity.name.en ?? '';
      return {
        key: `e${index}`,
        name: t(`${at}.name`, { ns, defaultValue: label(entity.name) }),
        description: entity.description ? t(`${at}.description`, { ns, defaultValue: label(entity.description) }) : '',
        kind: entityKindOf(nameEn),
        attrs: entityAttrsOf(nameEn, key).map((a) => ({
          key: a.key,
          label: a.ref ? head.name : t(`modDesign.model.entAttr.${a.label}`, { defaultValue: a.label }),
          type: `${t(`modDesign.types.${a.type}`, { defaultValue: a.type })}${a.ref ? ` → ${moduleName}` : ''}`,
          typeName: t(`modDesign.types.${a.type}`, { defaultValue: a.type }),
          ref: !!a.ref, required: a.required,
          options: (a.options ?? []).map((o) => t(`modDesign.model.entOpt.${o}`, { defaultValue: o })).join(', '),
        })),
      };
    });
    return [head, ...designed, ...types.map((rt) => typeEntity(rt, rt.kind === 'catalogue' ? 'catalogue' : 'child'))];
  }, [module, key, text, strings, t, label]);
}
